/**
 * Forgot-password page that sends a password-reset request to the backend.
 * Shows a confirmation message after successful submission.
 *
 * @module ForgotPasswordPage
 */

import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  Link,
  alpha,
  InputAdornment,
} from '@mui/material';
import { motion } from 'framer-motion';
import EmailIcon from '@mui/icons-material/Email';
import LockResetIcon from '@mui/icons-material/LockReset';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import api from '../api';
import { GREEN, DARK_BLUE } from '../theme';
import { FloatingOrb, PageTransition } from '../components/AnimatedComponents';

/** Password reset request form — collects email and shows success feedback. */
export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.post('/api/auth/password-reset-request', { email });
      setSent(true);
    } catch {
      setError(t('forgotPassword.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageTransition>
      <SEO
        title={t('forgotPassword.seoTitle')}
        description={t('forgotPassword.seoDesc')}
        canonical="/forgot-password"
        noindex
      />
      <Box
        sx={{
          minHeight: '80vh',
          display: 'flex',
          alignItems: 'center',
          py: 4,
          position: 'relative',
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${alpha(DARK_BLUE, 0.03)} 0%, ${alpha(GREEN, 0.03)} 100%)`,
        }}
      >
        <FloatingOrb size={350} color={GREEN} top={-80} left={-60} animationDelay="2s" />
        <FloatingOrb size={250} color={DARK_BLUE} bottom={-60} right={-40} animationDelay="5s" />

        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, md: 6 },
                boxShadow: '0 8px 40px rgba(0,0,0,0.08)',
                border: `1px solid ${alpha(DARK_BLUE, 0.06)}`,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: `linear-gradient(90deg, ${GREEN}, ${DARK_BLUE})`,
                }}
              />

              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                >
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      background: sent
                        ? `linear-gradient(135deg, ${GREEN}, #2d9c1c)`
                        : `linear-gradient(135deg, ${DARK_BLUE}, #004a5e)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2.5,
                      transition: 'background 0.5s ease',
                    }}
                  >
                    {sent ? (
                      <CheckCircleIcon sx={{ color: 'white', fontSize: 28 }} />
                    ) : (
                      <LockResetIcon sx={{ color: 'white', fontSize: 28 }} />
                    )}
                  </Box>
                </motion.div>
                <Typography variant="h3" sx={{ mb: 0.5, fontWeight: 700 }}>
                  {t('forgotPassword.title')}
                </Typography>
                <Typography color="text.secondary" sx={{ fontSize: '1rem' }}>
                  {t('forgotPassword.subtitle')}
                </Typography>
              </Box>

              {error && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                  <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
                </motion.div>
              )}
              {sent && (
                <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
                  <Alert severity="success" sx={{ mb: 3 }}>
                    {t('forgotPassword.success')}
                  </Alert>
                </motion.div>
              )}

              {!sent && (
                <form onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    label={t('forgotPassword.email')}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    sx={{ mb: 3 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    color="secondary"
                    size="large"
                    disabled={loading}
                    sx={{ mb: 3, py: 1.5, borderRadius: 3, fontSize: '1rem' }}
                  >
                    {loading ? t('forgotPassword.submitting') : t('forgotPassword.submit')}
                  </Button>
                </form>
              )}

              <Typography align="center" variant="body2" color="text.secondary">
                <Link component={RouterLink} to="/login" sx={{ fontWeight: 600 }}>
                  {t('forgotPassword.backToLogin')}
                </Link>
              </Typography>
            </Paper>
          </motion.div>
        </Container>
      </Box>
    </PageTransition>
  );
}
